import React from 'react';

/**
 * État vide partagé — généralisation du motif déjà en place sur « Mes enquêtes »
 * (icône, titre, explication, action), qui était le seul correct de l'app.
 *
 * L'audit du 20/08 a recensé 25 états vides dont 18 se contentaient d'une ligne
 * de texte gris centrée, sans dire à l'utilisateur quoi faire ensuite.
 *
 * Props :
 *  - icon      : composant d'icône lucide (optionnel)
 *  - title     : phrase courte, ce qui manque
 *  - message   : ce qu'il faut faire pour que ça se remplisse
 *  - action    : { label, onClick } — le bouton qui débloque la situation
 *  - variant   : 'card' (encadré pointillé, par défaut) | 'plain' (dans une carte existante)
 */
export default function EmptyState({ icon: Icon, title, message, action, variant = 'card', className = '' }) {
  const frame =
    variant === 'card'
      ? 'rounded-2xl border border-dashed border-base-200 bg-base-100 py-14'
      : 'py-10';

  // `aria-live` : le passage « je charge » -> « il n'y a rien » est une
  // information, et elle n'etait annoncee nulle part.
  return (
    <div className={`px-6 text-center ${frame} ${className}`} role="status" aria-live="polite">
      {Icon && <Icon size={40} className="mx-auto mb-3 text-base-content/25" />}
      {title && <h3 className="text-base font-semibold text-base-content">{title}</h3>}
      {message && <p className="mx-auto mt-1 max-w-md text-sm text-base-content/60">{message}</p>}
      {action && (
        <button type="button" onClick={action.onClick} className="btn btn-primary btn-sm mt-4 gap-2">
          {action.icon}
          {action.label}
        </button>
      )}
    </div>
  );
}

/**
 * Échec de chargement — à ne PAS confondre avec un état vide.
 *
 * Six écrans affichaient « Aucune donnée » quand l'API avait simplement échoué :
 * l'utilisateur en concluait que ses données avaient disparu.
 */
export function ErrorState({ message, onRetry, variant = 'card', className = '' }) {
  const frame =
    variant === 'card' ? 'rounded-2xl border border-base-200 bg-base-100 py-10' : 'py-8';

  // `role="alert"` : une panne doit interrompre, pas attendre son tour.
  return (
    <div className={`px-6 text-center ${frame} ${className}`} role="alert">
      <p className="text-sm text-base-content/70">
        {message || "Ces données n'ont pas pu être chargées."}
      </p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn btn-sm btn-outline mt-3">
          Réessayer
        </button>
      )}
    </div>
  );
}
